import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useSearch } from "@/contexts/SearchContext";
import { toast } from "sonner";
import { useT } from "@/i18n/LanguageContext";
import { EmailBouncedBadge } from "@/components/EmailBouncedBadge";

export default function Customers() {
  const { user } = useAuth();
  const { t } = useT();
  const { query, setPlaceholder } = useSearch();
  const qc = useQueryClient();
  const { data: rawData = [] } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", logo_url: "", shopify_id: "" });
  const create = useMutation({
    mutationFn: (d) => api.post("/customers", d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["customers"] }); setOpen(false); setForm({ name: "", email: "", phone: "", logo_url: "", shopify_id: "" }); toast.success("Customer created"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  useEffect(() => {
    setPlaceholder("Search customers — Name · Email · Phone · Shopify ID…");
  }, [setPlaceholder]);

  const data = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return rawData;
    return rawData.filter((c) => [c.name, c.email, c.phone, c.shopify_id]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [rawData, query]);

  const canCreate = user.role === "back_office" || user.role === "super_admin";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("Customers")}</h1>
          <p className="text-sm text-slate-500 mt-1">{data.length} customers registered.</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-customer-btn" className="bg-slate-900 hover:bg-slate-800">+ New Customer</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Customer</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {["name", "email", "phone", "logo_url", "shopify_id"].map((k) => (
                  <div key={k}>
                    <Label className="capitalize text-xs">{k.replace("_", " ")}</Label>
                    <Input data-testid={`customer-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" />
                  </div>
                ))}
                <Button data-testid="customer-create-submit" onClick={() => create.mutate(form)} className="w-full bg-slate-900 hover:bg-slate-800">Create</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{t("Customer")}</th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{t("Email")}</th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{t("Phone")}</th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Shopify</th>
            </tr>
          </thead>
          <tbody>
            {data.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                <td className="px-4 py-3 flex items-center gap-2">
                  {c.logo_url && <img src={c.logo_url} alt="" className="w-7 h-7 rounded border border-slate-200" />}
                  <span className="font-medium">{c.name}</span>
                </td>
                <td className="px-4 py-3 text-slate-600">{c.email || "—"} <EmailBouncedBadge customer={c} testid={`customer-email-bounced-${c.id}`} className="ml-1" /></td>
                <td className="px-4 py-3 text-slate-600">{c.phone || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-600">{c.shopify_id || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
